import constants from '@/Constants';
import { FormReport } from '@/modules/pdf/form-report';
import {
  FormReportResponse,
  SectionData,
} from '@/modules/pdf/form-report/types';
import { GoiasaReport } from '@/modules/pdf/goiasa';
import { GoiasaAlertReport } from '@/modules/pdf/goiasa-alert';
import { EventReportData as GoiasaAlertEventReportData } from '@/modules/pdf/goiasa-alert/types';
import { GoiasaDangersAndRisksReport } from '@/modules/pdf/goiasa-dangers-and-risks';
import {
  EventReportData as GoiasaDangersEventReportData,
  QuestionData as GoiasaQuestionData,
  SectionData as GoiasaSectionData,
  ItemData,
} from '@/modules/pdf/goiasa-dangers-and-risks/types';
import { GoiasaEfficiencyVerification } from '@/modules/pdf/goiasa-efficiency-verification';
import { EventReportData as GoiasaEfficiencyEventReportData } from '@/modules/pdf/goiasa-efficiency-verification/types';
import { GoiasaIncidentAnalysis } from '@/modules/pdf/goiasa-incident-analysis';
import { EventReportData as GoiasaIncidentEventReportData } from '@/modules/pdf/goiasa-incident-analysis/types';
import { GoiasaPermitAssessment } from '@/modules/pdf/goiasa-permit-assessment';
import { EventReportData as GoiasaPermitEventReportData } from '@/modules/pdf/goiasa-permit-assessment/types';
import { GoiasaRiskSeries } from '@/modules/pdf/goiasa-risk-series';
import { EventReportData as GoiasaRiskEventReportData } from '@/modules/pdf/goiasa-risk-series/types';
import { GoiasaSafetyObservation } from '@/modules/pdf/goiasa-safety-observation';
import { EventReportData as GoiasaSafetyEventReportData } from '@/modules/pdf/goiasa-safety-observation/types';
import { EventReportData as GoiasaEventReportData } from '@/modules/pdf/goiasa/types';
import axios from 'axios';
import React, { useEffect, useState } from 'react';

export const ReportFormularyQuestion: React.FC<{
  reportId: string;
  workspaceSlug: string;
}> = ({ reportId, workspaceSlug }) => {
  const [report, setReport] = useState<FormReportResponse | null>(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const url = constants.REPORTS_API_URL || '';

        const parsedUrl = url
          .toLowerCase()
          .replace('{{workspace}}', workspaceSlug);

        const { data, status } = await axios.get<FormReportResponse>(
          `${parsedUrl}pdf_json_by_export_id?export_id=${reportId}`,
          {
            headers: {
              Authorization: constants.REPORTS_API_AUTHORIZATION_TOKEN,
            },
          },
        );

        if (status === 200) {
          setReport(data);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchReport();
  }, [reportId, workspaceSlug]);

  /**
   * Parses the FormReportResponse body to the format expected by Goiasa reports
   * @param reportData The FormReportResponse data
   * @param isMultiItem Whether the report expects multiple items (for reports that use items array)
   * @returns The parsed data in the appropriate EventReportData format
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  const parseBody = <T extends object>(
    reportData: FormReportResponse | null,
    isMultiItem: boolean = false,
  ): T => {
    if (!reportData) {
      return ({
        header: {
          hide_score: false,
          logo: '',
          status: 'done' as const,
          fantasy_name: '',
          company_name: '',
          document: '',
          address: '',
          event_name: '',
          event_id: '',
          event_date_time: '',
          event_created_at: '',
          event_finished_at: '',
          created_at: '',
          event_type: '',
          author_full_name: '',
          author_email: '',
          total_score: 0,
          score_obtained: 0,
          percent_score: null,
          local: {
            name: '',
            city: '',
            state: '',
            address: '',
          },
          checklist_id: '',
        },
        body: isMultiItem ? { items: [] } : {},
      } as unknown) as T;
    }

    // Check if body is an array or an object with section IDs as keys
    const sections: any[] =
      !reportData?.body || Array.isArray(reportData.body)
        ? reportData.body
        : Object.values(reportData.body as Record<string, any>);

    // Convert FormReportResponse sections to Goiasa format
    const convertSection = (section: any): GoiasaSectionData => {
      // Handle both section formats (array items or object with questions)
      const questions = section.questions || [];

      return {
        section_name: section.name || '',
        section_description: section.description || '',
        position: section.position || 0,
        total_score: 0,
        score_obtained: 0,
        percent_score: null,
        questions: questions.map((q: any) => {
          // For template exports, create placeholder answers based on question type
          const createPlaceholderAnswers = () => {
            // For questions with options (radio, checkbox, etc.), use the first option as placeholder
            if (q.options && q.options.length > 0) {
              return [
                {
                  id: `placeholder-${q.id}`,
                  option: '',
                  red_flag: null,
                  is_flagged: null,
                  content: null,
                  comment: null,
                  question_parent_option_id: null,
                  position: 0,
                  score: 0,
                  collection_item: {
                    code: '',
                    description: '',
                    name: '',
                    collection: '',
                    properties: [],
                  },
                  geolocation: {
                    latitude: '',
                    longitude: '',
                  },
                },
              ];
            }

            // For text/content questions, create empty content placeholder
            return [
              {
                id: `placeholder-${q.id}`,
                option: null,
                red_flag: null,
                is_flagged: null,
                content: '', // Empty for template
                comment: null,
                question_parent_option_id: null,
                position: 0,
                score: 0,
                collection_item: {
                  code: '',
                  description: '',
                  name: '',
                  collection: '',
                  properties: [],
                },
                geolocation: {
                  latitude: '',
                  longitude: '',
                },
              },
            ];
          };

          return {
            image: q.image || q.imageUrl || '',
            name: q.name || '',
            type: q.questionType || 'text',
            position: q.position || 0,
            weight: q.weight || q.scoreWeight || 0,
            score_obtained: 0,
            likes: 0,
            dislikes: 0,
            answers: createPlaceholderAnswers(),
            all_feedbacks: [],
            question_id: q.id || q.name || '',
          } as GoiasaQuestionData;
        }),
      };
    };

    // Create a single item from all sections
    // For an empty formulary, we don't include scores
    const createSingleItem = (): ItemData => {
      return {
        collection_item: {
          code: '',
          description: '',
          name: reportData.header?.formulary_name || '',
          collection: '',
          properties: [],
        },
        form_name: reportData.header?.formulary_name || '',
        position: 0,
        total_score: 0, // Empty formulary has no scores
        score_obtained: 0, // Empty formulary has no scores
        percent_score: null, // Empty formulary has no scores
        collection_item_name: reportData.header?.formulary_name || '',
        sections: sections?.map(convertSection),
      };
    };

    // For multi-item reports, create an array of items
    // For an empty formulary, we don't include scores
    const createMultiItems = (): ItemData[] => {
      return sections?.map((section, index) => ({
        collection_item: {
          code: '',
          description: '',
          name: section.name || '',
          collection: '',
          properties: [],
        },
        form_name: reportData.header?.formulary_name || '',
        position: index,
        total_score: 0, // Empty formulary has no scores
        score_obtained: 0, // Empty formulary has no scores
        percent_score: null, // Empty formulary has no scores
        collection_item_name: section.name || '',
        sections: [convertSection(section)],
      }));
    };

    // Return the appropriate format based on isMultiItem flag
    // For an empty formulary, we don't include scores
    return ({
      header: {
        hide_score: true, // Hide scores for empty formulary
        logo: reportData.header?.logo || '',
        status: 'pending' as const, // Use pending status for empty formulary
        fantasy_name: reportData.header?.fantasy_name || '',
        company_name: reportData.header?.company_name || '',
        document: reportData.header?.document || '',
        address: reportData.header?.address || '',
        event_name: reportData.header?.formulary_name || '',
        event_id:
          (reportData.header as any)?.formulary_id ||
          reportData.header?.checklist_id ||
          '',
        event_created_at: '',
        event_date_time: reportData.header?.created_at || '',
        event_finished_at: '', // Empty for empty formulary
        event_type: reportData.header?.formulary_name || '',
        author_full_name: '',
        author_email: '',
        total_score: 0, // Empty formulary has no scores
        score_obtained: 0, // Empty formulary has no scores
        percent_score: null, // Empty formulary has no scores
        formulary_name: reportData.header?.formulary_name || '',
        local: {
          name: '',
          city: '',
          state: '',
          address: '',
        },
        checklist_id:
          (reportData.header as any)?.formulary_id ||
          reportData.header?.checklist_id ||
          '',
      },
      body: isMultiItem ? { items: createMultiItems() } : createSingleItem(),
    } as unknown) as T;
  };

  const getReport = () => {
    if (workspaceSlug !== 'goiasa') {
      return (
        <FormReport
          report={{
            body: {
              items: report?.body || [],
            },
            header: report?.header,
            workspace: workspaceSlug,
          }}
        />
      );
    }

    switch (report?.header?.formulary_name) {
      case 'Comunicado Acidente/Incidente':
        return (
          <GoiasaAlertReport
            reportData={parseBody<GoiasaAlertEventReportData>(report)}
          />
        );

      case 'FORMU-0967 - Avaliação Permissão de Trabalho':
        return (
          <GoiasaPermitAssessment
            reportData={parseBody<GoiasaPermitEventReportData>(report)}
          />
        );

      case 'FORMU - 0831 - Levantamento de Perigos e Riscos':
        return (
          <>
            <GoiasaDangersAndRisksReport
              reportData={parseBody<GoiasaDangersEventReportData>(report, true)}
            />
          </>
        );

      case 'FORMU 1112 - Análise de Ocorrências - Rev. 18-03-2025':
        return (
          <>
            <GoiasaIncidentAnalysis
              reportData={parseBody<GoiasaIncidentEventReportData>(
                report,
                true,
              )}
            />
          </>
        );

      case 'Série de Risco':
        return (
          <>
            <GoiasaRiskSeries
              reportData={parseBody<GoiasaRiskEventReportData>(report, true)}
            />
          </>
        );

      case 'FORMU 1112 - Verificação de Eficácia - Rev. 18-03-2025':
        return (
          <>
            <GoiasaEfficiencyVerification
              reportData={parseBody<GoiasaEfficiencyEventReportData>(
                report,
                true,
              )}
            />
          </>
        );

      case 'Observação de Segurança - TST':
        return (
          <GoiasaSafetyObservation
            reportData={parseBody<GoiasaSafetyEventReportData>(report)}
          />
        );

      default:
        return (
          <>
            <GoiasaReport
              reportData={parseBody<GoiasaEventReportData>(report)}
            />
          </>
        );
    }
  };

  return <div style={{ height: '100%', width: '100%' }}>{getReport()}</div>;
};
